// Importação do de-para UC → agrupamento por planilha (#5781, serve à carga da #5782).
// Nada é gravado antes da conferência: a planilha vira um plano (src/lib/agrupamentoFaturamento.ts),
// a tela mostra o que vincula, o que move e o que está errado, e só então aplica.
// A gravação vai pela mesma RPC da tela, uma chamada por agrupamento, e pode ser repetida
// sem duplicar.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../../src/lib/supabase';
import {
  MOTIVO_IMPORTACAO,
  BLOQUEIA_IMPORTACAO,
  interpretarResultadoVinculo,
  lotesDaImportacao,
  planejarImportacao,
  type LinhaImportacao,
  type PlanoImportacao,
  type UcParaPendencia,
} from '../../src/lib/agrupamentoFaturamento';
import type { AgrupamentoFaturamento } from '../../types';

const CAMPO =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

interface Props {
  aberto: boolean;
  agrupamentos: AgrupamentoFaturamento[];
  ucs: (UcParaPendencia & { id: number })[];
  vinculoAtual: Map<number, number>;
  onFechar: () => void;
  onAplicado: (msg: string) => void;
}

interface Planilha {
  nomeArquivo: string;
  abas: string[];
  aba: string;
  colunas: string[];
  linhas: Record<string, unknown>[];
}

/** Lê uma aba e devolve colunas + linhas, com o chute inicial das duas colunas que importam. */
function lerAba(wb: XLSX.WorkBook, aba: string) {
  const linhas = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[aba], { defval: '' });
  const colunas = linhas.length > 0 ? Object.keys(linhas[0]) : [];
  const achar = (...termos: string[]) =>
    colunas.find(c => termos.some(t => c.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(t))) ?? '';
  return { linhas, colunas, colUc: achar('uc (numero novo)', 'numero da uc', 'uc'), colCod: achar('cod. agrupamento', 'agrupamento', 'codigo') };
}

/** Aba mais provável: a que tem as duas colunas e mais linhas. A planilha real do de-para começa
 *  com uma aba "Resumo", então pegar sempre a primeira leva o usuário para a aba errada. */
function melhorAba(wb: XLSX.WorkBook): string {
  let melhor = wb.SheetNames[0];
  let placar = -1;
  for (const nome of wb.SheetNames) {
    const { linhas, colUc, colCod } = lerAba(wb, nome);
    const nota = (colUc && colCod ? 1_000_000 : 0) + linhas.length;
    if (nota > placar) { placar = nota; melhor = nome; }
  }
  return melhor;
}

const ImportarAgrupamentoModal: React.FC<Props> = ({ aberto, agrupamentos, ucs, vinculoAtual, onFechar, onAplicado }) => {
  const [planilha, setPlanilha] = useState<Planilha | null>(null);
  const [livro, setLivro] = useState<XLSX.WorkBook | null>(null);
  const [colUc, setColUc] = useState('');
  const [colCod, setColCod] = useState('');
  const [lendo, setLendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const botaoEscolher = useRef<HTMLButtonElement>(null);
  const tituloId = React.useId();

  // Esc fecha e o foco inicial vai pro botão principal, como no modal de cadastro do módulo.
  useEffect(() => {
    if (!aberto) return;
    setTimeout(() => botaoEscolher.current?.focus(), 0);
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape' && !aplicando) fechar(); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, aplicando]);

  const plano: PlanoImportacao | null = useMemo(() => {
    if (!planilha || !colUc || !colCod) return null;
    const linhas: LinhaImportacao[] = planilha.linhas.map((l, i) => ({
      linha: i + 2, // +2: linha 1 é o cabeçalho e a planilha é 1-based
      numero_uc: String(l[colUc] ?? ''),
      cod_agrupamento: String(l[colCod] ?? ''),
    }));
    return planejarImportacao(linhas, ucs, agrupamentos, vinculoAtual);
  }, [planilha, colUc, colCod, ucs, agrupamentos, vinculoAtual]);

  if (!aberto) return null;

  const fechar = () => {
    if (aplicando) return;
    setPlanilha(null); setLivro(null); setColUc(''); setColCod(''); setErro(null);
    onFechar();
  };

  const lerArquivo = async (arquivo: File) => {
    setLendo(true);
    setErro(null);
    try {
      const wb = XLSX.read(await arquivo.arrayBuffer(), { type: 'array' });
      if (wb.SheetNames.length === 0) throw new Error('A planilha não tem nenhuma aba.');
      setLivro(wb);
      const aba = melhorAba(wb);
      const { linhas, colunas, colUc: uc, colCod: cod } = lerAba(wb, aba);
      if (linhas.length === 0) throw new Error(`A aba "${aba}" está vazia. Esperado: cabeçalho na linha 1 e uma linha por UC.`);
      setPlanilha({ nomeArquivo: arquivo.name, abas: wb.SheetNames, aba, colunas, linhas });
      // Chute inicial: o usuário confere a aba e as duas colunas nos seletores.
      setColUc(uc);
      setColCod(cod);
    } catch (e: any) {
      setPlanilha(null);
      setErro(e?.message || 'Não foi possível ler o arquivo.');
    } finally {
      setLendo(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const aplicar = async () => {
    if (!plano?.podeAplicar) return;
    setAplicando(true);
    setErro(null);
    try {
      let vinculadas = 0, movidas = 0, jaEstavam = 0;
      for (const lote of lotesDaImportacao(plano)) {
        const { data, error } = await supabase.rpc('vincular_ucs_agrupamento', {
          p_agrupamento_id: lote.agrupamento_id,
          p_uc_ids: lote.uc_ids,
          p_motivo: `Importação de planilha (${planilha?.nomeArquivo ?? 'sem nome'})`,
        });
        if (error) throw error;
        const r = interpretarResultadoVinculo(data);
        vinculadas += r.vinculadas; movidas += r.movidas; jaEstavam += r.ja_estavam;
      }
      onAplicado(
        vinculadas + movidas === 0
          ? 'Nenhuma alteração: as UCs da planilha já estavam como pedido.'
          : `Importação aplicada: ${vinculadas} vinculada(s), ${movidas} movida(s)${jaEstavam ? `, ${jaEstavam} sem alteração` : ''}.`,
      );
      fechar();
    } catch (e: any) {
      // Cada lote é uma transação: o que já gravou continua gravado. Reaplicar é seguro.
      setErro(`${e?.message || 'Falha ao aplicar.'} As UCs já gravadas permanecem; corrija e importe de novo — repetir não duplica.`);
    } finally {
      setAplicando(false);
    }
  };

  const problemasQueBloqueiam = plano?.problemas.filter(p => BLOQUEIA_IMPORTACAO.includes(p.motivo)) ?? [];
  const avisos = plano?.problemas.filter(p => !BLOQUEIA_IMPORTACAO.includes(p.motivo)) ?? [];

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby={tituloId}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-800">
        <header className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          <h2 id={tituloId} className="text-base font-bold text-gray-900 dark:text-white">Importar de-para por planilha</h2>
          <button type="button" onClick={fechar} aria-label="Fechar" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            A planilha precisa de cabeçalho na primeira linha, uma coluna com o número da UC e outra com o
            código do agrupamento. Nada é gravado antes de você conferir o resumo.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              ref={botaoEscolher}
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={lendo || aplicando}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {lendo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {planilha ? 'Trocar arquivo' : 'Escolher arquivo'}
            </button>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="sr-only"
              onChange={e => { const f = e.target.files?.[0]; if (f) lerArquivo(f); }} />
            {planilha && <span className="text-sm text-gray-500 dark:text-gray-400">{planilha.nomeArquivo} · {planilha.linhas.length} linha(s)</span>}
          </div>

          {planilha && planilha.abas.length > 1 && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="imp-aba">Aba da planilha</label>
              <select id="imp-aba" value={planilha.aba} className={`${CAMPO} w-full sm:w-auto`}
                onChange={e => {
                  if (!livro) return;
                  const aba = e.target.value;
                  const { linhas, colunas, colUc: uc, colCod: cod } = lerAba(livro, aba);
                  setPlanilha({ ...planilha, aba, colunas, linhas });
                  setColUc(uc); setColCod(cod);
                  setErro(linhas.length === 0 ? `A aba "${aba}" está vazia.` : null);
                }}>
                {planilha.abas.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-gray-400">
                O arquivo tem {planilha.abas.length} abas. Escolhi a que parece ser o de-para; confira.
              </p>
            </div>
          )}

          {planilha && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="imp-col-uc">Coluna do número da UC</label>
                <select id="imp-col-uc" value={colUc} onChange={e => setColUc(e.target.value)} className={`${CAMPO} w-full`}>
                  <option value="">Selecione</option>
                  {planilha.colunas.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300" htmlFor="imp-col-cod">Coluna do código do agrupamento</label>
                <select id="imp-col-cod" value={colCod} onChange={e => setColCod(e.target.value)} className={`${CAMPO} w-full`}>
                  <option value="">Selecione</option>
                  {planilha.colunas.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}

          {plano && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { t: 'A vincular', v: plano.aVincular, c: 'text-emerald-700 dark:text-emerald-400' },
                  { t: 'A mover de grupo', v: plano.aMover, c: 'text-amber-700 dark:text-amber-400' },
                  { t: 'Sem mudança', v: plano.semMudanca, c: 'text-gray-700 dark:text-gray-300' },
                ].map(x => (
                  <div key={x.t} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{x.t}</p>
                    <p className={`mt-1 text-2xl font-bold tabular-nums ${x.c}`}>{x.v}</p>
                  </div>
                ))}
              </div>

              {Object.keys(plano.porAgrupamento).length > 0 && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {Object.entries(plano.porAgrupamento).sort((a, b) => b[1] - a[1]).map(([cod, n]) => (
                    <span key={cod} className="rounded-full bg-gray-100 px-3 py-1 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      {agrupamentos.find(a => (a.codigos ?? []).some(c => c.toUpperCase() === cod.toUpperCase()))?.nome ?? cod} ({cod}): {n}
                    </span>
                  ))}
                </div>
              )}

              {problemasQueBloqueiam.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-900/20" role="alert">
                  <p className="flex items-center gap-2 font-semibold text-red-800 dark:text-red-300">
                    <AlertTriangle size={16} /> {problemasQueBloqueiam.length} linha(s) impedem a importação
                  </p>
                  <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-red-800 dark:text-red-300">
                    {problemasQueBloqueiam.slice(0, 50).map((p, i) => (
                      <li key={`${p.linha}-${i}`}>Linha {p.linha}{p.numero_uc ? ` (UC ${p.numero_uc})` : ''}: {MOTIVO_IMPORTACAO[p.motivo]}</li>
                    ))}
                    {problemasQueBloqueiam.length > 50 && <li>… e mais {problemasQueBloqueiam.length - 50}.</li>}
                  </ul>
                </div>
              )}

              {avisos.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                  {avisos.length} aviso(s): {MOTIVO_IMPORTACAO.uc_repetida_na_planilha}.
                </div>
              )}

              {plano.podeAplicar && problemasQueBloqueiam.length === 0 && (
                <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 size={16} /> Planilha conferida. Nada foi gravado ainda.
                </p>
              )}

              {!plano.podeAplicar && problemasQueBloqueiam.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Não há nada a aplicar: todas as UCs da planilha já estão nos agrupamentos indicados.
                </p>
              )}
            </>
          )}

          {erro && (
            <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">{erro}</p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4 dark:border-gray-700">
          <button type="button" onClick={fechar} disabled={aplicando} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200">
            Cancelar
          </button>
          <button
            type="button"
            onClick={aplicar}
            disabled={!plano?.podeAplicar || aplicando}
            aria-busy={aplicando}
            title={!plano ? 'Escolha o arquivo e as duas colunas' : !plano.podeAplicar ? 'Corrija as linhas apontadas acima' : undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {aplicando && <Loader2 size={15} className="animate-spin" />}
            {aplicando ? 'Aplicando...' : plano ? `Aplicar ${plano.aVincular + plano.aMover} alteração(ões)` : 'Aplicar'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default ImportarAgrupamentoModal;
